import app from "@/main";
import {ITask} from "../store/modules/GorevYoneticisiTypes";
import {TakipSorgulaTaskCevap} from "../store/modules/TaskHandler/TakipSorgulaTaskHandler";
import {DosyaBorclu, EmptyObject} from "../uyap/DosyaBorclu";
import {BorcluTur, IKurumDVO} from "../uyap/CommonInterfaces";
import {SnapshotBorclu, SnapshotPaketi} from "./types";
import {dosyaEsasNoNormalize} from "./DosyaRefHafizasi";
import {normalizerBul, fallbackHash} from "./normalizers";
import {kanonikHash} from "./kanonik";

// DosyaBorclu uyesi -> picker `name` degeri. hisse ve takyidat BILEREK yok
// (takbis'e katlanmis, ayri paket uretmezler). constructor.name YASAK — tablo acik.
const ALAN_ESLEME: Record<string, string> = {
    ssk: "sgkssk",
    egm: "egm",
    mernis: "mernis",
    mersis: "mersis",
    sgkkamu: "sgkkamu",
    gib: "gib",
    sgkbagkur: "sgkbagkur",
    bankaBilgileri: "bankahesap",
    dib: "dib",
    sgkIsyeri: "sgkisyeri",
    takbis: "takbis",
    sgkhaciz: "sgkhaciz",
    bes: "bes",
    iski: "iski",
    icraDosyaKaydi: "icradosyakaydi",
    gsm: "gsm",
    postaCeki: "postaceki",
    sgkMossip: "sgkmossip",
    emekliBagkur: "emeklibagkur",
    emekliSsk: "emeklissk",
    emekliKamu: "emeklikamu",
    // DEV-3512 (develop): ücretli firma sorgusu
    firma: "firma",
};

export class SnapshotYazici {
    /** K4: gövdeli paketlerde tek POST'un kayıt sayısı tavanı. */
    static readonly POST_DILIM_BOYU = 25;

    // handler'dan cagrilir; ASLA throw etmez.
    static async yaz(task: ITask, cevap: TakipSorgulaTaskCevap): Promise<void> {
        try {
            // Kararli dosya kimligi birim_id + dosya_esas_no ikilisidir — sorgular UYAP
            // dosyalari uzerinden yapilir, gorevde icrapro takip_id'si COGU ZAMAN YOKTUR
            // ve anahtar ona dayanmaz. Ikili eksikse paket kurulamaz, sessiz cikilir.
            if (!task || !task.birim_id || !task.dosya_esas_no) {
                return;
            }
            if (!cevap || !Array.isArray(cevap.borclular) || cevap.borclular.length === 0) {
                return;
            }

            const paketler: SnapshotPaketi[] = [];
            const fetchedAt = new Date().toISOString();

            for (const borclu of cevap.borclular) {
                if (!borclu) continue;

                // Tek borclunun bozuk verisi TUM koşumun paketlerini dusurmemeli:
                // her borclu kendi try/catch'i icinde islenir.
                try {
                    SnapshotYazici.borcluPaketleriEkle(borclu, task, fetchedAt, paketler);
                } catch (e) {
                    console.warn(`SnapshotYazici: borclu ${borclu.kisiKurumId} islenemedi, atlandi.`, e);
                }
            }

            if (paketler.length === 0) {
                return;
            }

            // K4 sonrası paketler ham gövde taşıdığı için tek istek belirgin
            // büyüyebilir (N dosya × 20 alan × gövde). PHP post_max_size /
            // proxy sınırına takılmamak için dilimlenerek gönderilir.
            const dilimler: SnapshotPaketi[][] = [];
            for (let i = 0; i < paketler.length; i += SnapshotYazici.POST_DILIM_BOYU) {
                dilimler.push(paketler.slice(i, i + SnapshotYazici.POST_DILIM_BOYU));
            }

            // B01: dilimler artık PARALEL gönderilir — sıralı `await` (5 borçlu
            // × 21 alan → 5 ayrı POST turu) görev bitişini gereksiz uzatıyordu.
            // Promise.allSettled: bir dilimin hatası diğerlerini düşürmez, en-iyi-
            // çaba sözleşme korunur (koşum sonu yazımı; sonraki koşum aynı
            // üçlüleri idempotent tazeler).
            const sonuclar = await Promise.allSettled(
                dilimler.map((dilim) => app.$http.post("/api/v1/sorgu-snapshot", dilim))
            );
            sonuclar.forEach((sonuc, i) => {
                if (sonuc.status === "rejected") {
                    console.warn(`SnapshotYazici: ${i + 1}. dilim yazilamadi, kalanlarla devam.`, sonuc.reason);
                }
            });
        } catch (e) {
            console.warn("SnapshotYazici: snapshot yazilamadi.", e);
        }
    }

    private static borcluPaketleriEkle(
        borclu: DosyaBorclu,
        task: ITask,
        fetchedAt: string,
        paketler: SnapshotPaketi[]
    ): void {
        const borcluBilgisi = SnapshotYazici.borcluPaketi(borclu);

        for (const uye of Object.keys(ALAN_ESLEME)) {
            const alan = ALAN_ESLEME[uye];
            const deger = (borclu as any)[uye];

            if (deger === null || deger === undefined) continue;
            if (deger instanceof EmptyObject) continue;

            let entities: SnapshotPaketi["entities"] = [];
            let ozet: SnapshotPaketi["ozet"];
            let hash: string;

            try {
                const normalizer = normalizerBul(alan);
                if (normalizer) {
                    const sonuc = normalizer(deger);
                    entities = sonuc.entities || [];
                    ozet = sonuc.ozet;
                    hash = kanonikHash(entities);
                } else {
                    entities = [];
                    hash = fallbackHash(deger);
                }
            } catch (e) {
                console.warn(`SnapshotYazici: '${alan}' alani islenemedi, fallback'e dusuldu.`, e);
                entities = [];
                hash = fallbackHash(deger);
            }

            paketler.push({
                birimId: String(task.birim_id),
                // B2: görev kaydına UYAP'ın "2023/100840 - ( 2015/7071 )" gibi
                // birleşen-dosya gösterim adı sızabiliyor; anahtar normalize edilir.
                dosyaEsasNo: dosyaEsasNoNormalize(task.dosya_esas_no),
                // takip eslesmesi varsa tasinir (ileriki eslestirme isine veri);
                // "0" ve bos degerler null'a indirgenir.
                takipId: task.takip_id && String(task.takip_id) !== "0" ? Number(task.takip_id) : null,
                alan,
                borclu: borcluBilgisi,
                kaynak: "gorev",
                fetchedAt,
                hash,
                entities,
                ozet,
                // K4: parse edilmiş ham cevap olduğu gibi taşınır (IndexedDB
                // eşleniği). Hash'e girmez; senkronun veri kaynağıdır.
                govde: deger,
            });
        }
    }

    private static borcluPaketi(borclu: DosyaBorclu): SnapshotBorclu {
        const paket: SnapshotBorclu = {
            kisiKurumId: borclu.kisiKurumId,
            turu: borclu.turu,
        };
        // getAd() borcluBilgileri eksikse patlar; ad yalniz rapor etiketi, kimlik degil.
        try {
            const ad = borclu.getAd();
            if (ad) paket.adSoyad = ad;
        } catch (e) {
            console.warn(`SnapshotYazici: borclu ${borclu.kisiKurumId} adi okunamadi.`, e);
        }
        // Kurumda vergiNo maskesiz gelir ve bos string de olsa aktarilir (sunucu cozumler);
        // yalniz borcluBilgileri hic yoksa alan eklenmez.
        if (borclu.turu === BorcluTur.KURUM && borclu.borcluBilgileri) {
            paket.vergiNo = (borclu.borcluBilgileri as IKurumDVO).vergiNo;
        }
        return paket;
    }
}
